import React, { useEffect, useState } from 'react';

import Button, { AddButton, CancelButton } from '../../../components/Button';
import CommonDataGrid from '../../../components/CommonDataGrid';
import { Column } from '../../../components/CommonDataGrid/props';
import ConfirmationDialog from '../../../components/DialogBox';
import Modal from '../../../components/Model';
import DeleteImg from '../../../images/SideBar/Delete';
import EditImg from '../../../images/SideBar/Edit';
import { apiPaths } from '../../../service/apiPaths';
import ApiService from '../../../service/ApiService';

interface IInterests {
  id: string;
  name: string;
  description: string;
}

export default function Interests() {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string>("");
  const [addData, setAddData] = useState<any>({
    name: "",
    description: "",
  });
  const [editData, setEditData] = useState<any>({
    name: "",
    description: "",
  });
  const [data, setData] = useState<IInterests[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchBar, setSearchBar] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await ApiService({
        endpoint: apiPaths.interests,
        method: "GET",
      });
      if (response) {
        setData(response);
        setTotalPages(Math.ceil(response.length / 20));
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    try {
      await ApiService({
        endpoint: apiPaths.interestsCreate,
        method: "POST",
        data: { type: addData.name, description: addData.description },
      });
      setIsAddModalOpen(false);
      setAddData({ name: "", description: "" });
      fetchData();
    } catch (error) {
      console.error("Error adding data:", error);
    }
  };

  const handleEdit = async () => {
    try {
      await ApiService({
        endpoint: `${apiPaths.interestsEdit}${selectedId}`,
        method: "PUT",
        data: { type: editData.name, description: editData.description },
      });
      setIsEditModalOpen(false);
      setEditData({ name: "", description: "" });
      fetchData();
    } catch (error) {
      console.error("Error editing data:", error);
    }
  };

  const handleDelete = async () => {
    try {
      await ApiService({
        endpoint: `${apiPaths.interestsRemove}${selectedId}`,
        method: "DELETE",
      });
      setIsDeleteModalOpen(false);
      fetchData();
    } catch (error) {
      console.error("Error deleting data:", error);
    }
  };

  const handleEditClick = (row: IInterests) => {
    setSelectedId(row.id);
    setEditData({
      name: row.name,
      description: row.description,
    });
    setIsEditModalOpen(true);
  };

  const handleDeleteClick = (id: string) => {
    setSelectedId(id);
    setIsDeleteModalOpen(true);
  };

  const Columns: Column[] = [
    {
      field: "name",
      headerName: "name",
      width: 50,
      align: "center",
    },
    {
      field: "description",
      headerName: "description",
      width: 50,
      align: "center",
    },
    {
      field: "Actions",
      headerName: "Actions",
      width: 50,
      align: "center",
      renderCell: (params: any) => (
        <div className="flex space-x-2 justify-center">
          <button
            className="text-fontColor hover:text-primary-2"
            onClick={() => handleEditClick(params.row)}
          >
            <EditImg />
          </button>
          <button
            className="text-red-500 hover:text-red-700"
            onClick={() => handleDeleteClick(params.row.id)}
          >
            <DeleteImg />
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="grid justify-items-end -mb-16 mr-5">
        <AddButton
          onClick={() => setIsAddModalOpen(true)}
          className=""
          children="+"
          type="button"
        />
      </div>

      {/* Data Table */}
      <div className="mt-4">
        <CommonDataGrid
          rows={data}
          columns={Columns}
          loading={loading}
          getRowId={(row) => row.id}
          showConfirmation={isDeleteModalOpen}
          confirmationMessage="Are you sure you want to delete this interest?"
          onCancelDelete={() => setIsDeleteModalOpen(false)}
          onConfirmDelete={handleDelete}
          setSearchBar={setSearchBar}
          setCurrentPage={setCurrentPage}
          currentPage={currentPage}
          totalPages={totalPages}
          tableName="Interests"
          dayFilter={false}
          downloadCSVFilter={false}
          searchFilter={false}

        />
      </div>

      {/* Add Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        style="w-96"
      >
        <div className="p-4">
          <h2 className="text-xl font-bold mb-4">Add Interests</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Name
              </label>
              <input
                type="text"
                value={addData.name}
                onChange={(e) =>
                  setAddData({ ...addData, name: e.target.value })
                }
                className="mt-1 block w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-primary-2 focus:ring-primary-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                value={addData.description}
                rows={4}
                onChange={(e) =>
                  setAddData({ ...addData, description: e.target.value })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-2 focus:ring-primary-2"
              />
            </div>
            <div className="flex justify-end space-x-2">
            <CancelButton
                onClick={() => setIsEditModalOpen(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
                type='button'
              >
                Cancel
              </CancelButton>
              <Button
                onClick={handleAdd}
                className="px-4 py-2 text-white bg-primary-2 rounded hover:bg-primary-3"
                type="button"
              >
                Add
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        style="w-96"
      >
        <div className="p-4">
          <h2 className="text-xl font-bold mb-4">Edit Interests</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Name
              </label>
              <input
                type="text"
                value={editData.name}
                onChange={(e) =>
                  setEditData({ ...editData, name: e.target.value })
                }
                className="mt-1 block w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-primary-2 focus:ring-primary-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                value={editData.description}
                rows={4}
                onChange={(e) =>
                  setEditData({ ...editData, description: e.target.value })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-2 focus:ring-primary-2"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <CancelButton
                onClick={() => setIsEditModalOpen(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
                type='button'
              >
                Cancel
              </CancelButton>
              <Button
                onClick={handleEdit}
                className="px-4 py-2 text-white bg-primary-2 rounded hover:bg-primary-3"
                type="button"
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Dialog */}
      {isDeleteModalOpen && (
        <ConfirmationDialog
          message="Are you sure you want to delete this interest?"
          onCancel={() => setIsDeleteModalOpen(false)}
          onConfirm={handleDelete}
        />
      )}
    </>
  );
}
